import { useMemo } from "react";
import { isEmpty } from "lodash";
import { E_ChannelOptions } from "../../globalTypes/GlobalTypes";
import {
  GeneralBannerType,
  T_CenterBannerData,
  T_FooterBannerData,
  T_TopBannerData,
} from "../../features/Features_Cms/CmsGlobal.Types";

/**
 * Shared utility functions for banner filtering across hooks
 * Extracted common logic to reduce duplication
 */

export interface PlatformFlags {
  isSuperApp: boolean;
  isMobileApp: boolean;
  isWeb: boolean;
}

export interface UserLobInfo {
  lobCodes: string[];
  channels: string[];
  userLobTag: "NTB" | "2FA-ND" | "OTHERS";
}

/**
 * Hook to compute platform flags
 */
export const usePlatformFlags = (
  channel: E_ChannelOptions,
  deviceName?: string,
): PlatformFlags => {
  return useMemo(() => {
    const isSuperApp = channel === E_ChannelOptions.SUPER_APP;
    const isMobileApp = !isEmpty(deviceName);
    return {
      isSuperApp,
      isMobileApp,
      isWeb: !isSuperApp && !isMobileApp,
    };
  }, [channel, deviceName]);
};

/**
 * Hook to extract LOB codes and channels from user details
 */
export const useUserLobInfo = (
  customerAndLobDetails: any[] = [],
  isNtbUser?: boolean,
  isUserRegistered?: boolean,
): UserLobInfo => {
  const lobCodes = useMemo(() => {
    const codes = Array.from(
      new Set(
        customerAndLobDetails.map((item) => item.LOB_CODE).filter(Boolean),
      ),
    );
    if (isNtbUser) codes.push("NTB");
    if (!isNtbUser && !isUserRegistered) codes.push("2FA-ND");
    return codes;
  }, [customerAndLobDetails, isNtbUser, isUserRegistered]);

  const channels = useMemo(() => {
    return Array.from(
      new Set(
        customerAndLobDetails
          .map((item) => item.CHANNEL?.toLowerCase())
          .filter(Boolean),
      ),
    );
  }, [customerAndLobDetails]);

  const userLobTag = useMemo(() => {
    if (isNtbUser) return "NTB";
    if (!isNtbUser && !isUserRegistered) return "2FA-ND";
    return "OTHERS";
  }, [isNtbUser, isUserRegistered]);

  return { lobCodes, channels, userLobTag };
};

/**
 * Normalize string to array
 */
export const normalizeStringToArray = (data: string): string[] => {
  if (isEmpty(data)) return [];
  return data.split(",").map((id: string) => id.trim());
};

/**
 * Sort array by sequence attribute
 */
export const sortBySequence = <T extends { attributes: { sequence: number } }>(
  arr: T[],
): T[] => {
  return [...arr].sort(
    (a, b) => Number(a.attributes.sequence) - Number(b.attributes.sequence),
  );
};

/**
 * Filter items by user LOB cohorts
 */
export const filterByUserLob = <
  T extends { attributes: { lobCohorts?: string } },
>(
  arr: T[],
  userLobTag: string,
  lobCodes: string[],
): T[] => {
  return arr.filter((item) => {
    const lobValues = normalizeStringToArray(
      item?.attributes?.lobCohorts || "",
    );
    if (lobValues.length === 0) return false;

    if (userLobTag === "OTHERS") {
      return lobValues.some((lob) => lobCodes.includes(lob));
    }
    return lobValues.includes(userLobTag);
  });
};

/**
 * Check if platform matches required platforms
 */
export const getIsPlatformValid = (
  platformString: string,
  platformFlags: PlatformFlags,
): boolean => {
  const requiredPlatforms =
    platformString
      ?.toLowerCase()
      .split(",")
      .map((p) => p.trim()) || [];
  return (
    (requiredPlatforms.includes("web") && platformFlags.isWeb) ||
    (requiredPlatforms.includes("mobile") && platformFlags.isMobileApp) ||
    (requiredPlatforms.includes("super app") && platformFlags.isSuperApp)
  );
};

/**
 * Check if channel is valid (empty match means valid)
 */
export const getIsChannelValid = (
  channelString: string,
  userChannels: string[],
): boolean => {
  const cardChannels =
    channelString?.split(",").map((channel) => channel?.trim().toLowerCase()) ||
    [];
  const matchedChannelData = userChannels.filter((channel) =>
    cardChannels.includes(channel),
  );
  return isEmpty(matchedChannelData);
};

export const getIsExactLobValid = (
  lobIds: string, // CMS lob code string
  lobCodes: string[], // user lob code array
  userLobTag: string = "OTHERS",
): boolean => {
  const lobValues = normalizeStringToArray(lobIds);
  // Check if ALL user LOB codes are present in the banner's LOB IDs
  if (userLobTag === "OTHERS") {
    return lobCodes.every((code) => lobValues.includes(code));
  } else {
    return lobValues.includes(userLobTag);
  }
};

export const getIsSomeLobValid = (
  lobIds: string, // CMS lob code string
  lobCodes: string[], // user lob code array
  userLobTag: string = "OTHERS",
): boolean => {
  const lobValues = normalizeStringToArray(lobIds);
  // Check if Some user LOB codes are present in the banner's LOB IDs
  if (userLobTag === "OTHERS") {
    return lobCodes.some((code) => lobValues.includes(code));
  } else {
    return lobValues.includes(userLobTag);
  }
};

export const getLeadCreationFlags = (data: GeneralBannerType) => {
  const allowedLeadCreation: string[] =
    data?.attributes?.leadCreation
      ?.split(",")
      .map((lead: string) => lead?.trim().toLowerCase()) ?? [];

  const enableSso = !!data?.attributes?.enableSso;

  return {
    isEnableSso: enableSso,
    isWebAllowed: allowedLeadCreation.includes("web"),
    isAppAllowed: allowedLeadCreation.includes("app"),
    isWebLead: enableSso && allowedLeadCreation.includes("web"),
    isAppLead: enableSso && allowedLeadCreation.includes("app"),
  };
};

export const hasBannerMetrics = (
  data: any,
): data is T_TopBannerData | T_CenterBannerData | T_FooterBannerData => {
  return (
    data && typeof data === "object" && ("amount" in data || "roi" in data)
  );
};

export const normalize = (val?: string) => (val || "").toLowerCase();

export const getBannerCategory = (data: GeneralBannerType) => {
  const bannerName = normalize(data?.attributes?.bannerName);
  const bannerType = normalize(data?.attributes?.bannerType);
  const bannerCategory = normalize(data?.attributes?.bannerCategory);
  const sequence = data?.attributes?.sequence;
  const sequenceStr = String(sequence || "");

  if (bannerName === "top banner") {
    if (bannerType === "priority banner" || bannerType === "default banner") {
      return `${bannerCategory} ${sequenceStr}`;
    }
    if (bannerType === "unified offer mart") {
      return bannerCategory;
    }
  } else if (bannerName === "footer banner") {
    if (bannerType === "employee banner" || bannerType === "default banner") {
      return `${bannerCategory} ${sequenceStr}`;
    }
  } else if (bannerName === "center banner") {
    if (bannerType === "promotional banner") {
      return `${bannerCategory} ${sequence}`;
    }
    if (bannerType === "li_pasa banner") {
      return bannerCategory;
    }
  }

  return bannerCategory;
};
